import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(next);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO path="/login" title="Sign in" description="Sign in to your What Do We Want? When Do We Want It? account to submit essays, polls and forum posts." />
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="mono text-xs uppercase tracking-[0.3em] text-amber-700">Members</div>
        <h1 className="serif text-5xl mt-3">Sign in</h1>
        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none text-lg"
            />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none text-lg"
            />
          </div>
          {error && <p data-testid="login-error" className="text-red-800 mono text-xs">{error}</p>}
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-black text-white py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors disabled:opacity-40"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-8 text-sm text-neutral-600">
          Not yet a member? <Link to="/register" className="edi-link">Join the discussion</Link>.
        </p>
      </div>
    </Layout>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(name, email, password);
      toast.success("Welcome to the paper.");
      navigate("/");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO path="/register" title="Join" description="Register to contribute essays, take part in debates, vote in polls and add your voice to a moderated forum on politics and media." />
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="mono text-xs uppercase tracking-[0.3em] text-amber-700">Members</div>
        <h1 className="serif text-5xl mt-3">Join the paper</h1>
        <p className="text-neutral-700 mt-3">Add your voice. Cast your vote. Read moderated, thoughtful opinion.</p>
        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Display name</label>
            <input
              data-testid="register-name-input"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none text-lg"
            />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Email</label>
            <input
              data-testid="register-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none text-lg"
            />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Password (min. 6 chars)</label>
            <input
              data-testid="register-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none text-lg"
            />
          </div>
          {error && <p data-testid="register-error" className="text-red-800 mono text-xs">{error}</p>}
          <button
            type="submit"
            data-testid="register-submit-button"
            disabled={loading}
            className="w-full bg-black text-white py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors disabled:opacity-40"
          >
            {loading ? "Joining…" : "Create account"}
          </button>
        </form>
        <p className="mt-8 text-sm text-neutral-600">
          Already a member? <Link to="/login" className="edi-link">Sign in</Link>.
        </p>
      </div>
    </Layout>
  );
}
